
import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  CircularProgress
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import "./CreateSiteMember.css";

const CreateSiteMember = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    customer_id: "",
    site_id: "",
    name: "",
    email: "",
    phone_no: "",
    designation: "",
    notify_sms: false,
    notify_email: false,
  });

  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(true);
  const [siteLoading, setSiteLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const API_URL = process.env.REACT_APP_API_DJANGO_URL;
  const token = localStorage.getItem("accessToken");
  // const customerId = localStorage.getItem("customer_id");






  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setCustomerLoading(true);
    try {
      const customerRes = await axios.get(
        `${API_URL}/api/get_customers/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      setCustomers(customerRes.data || []);
      setCustomers(customerRes.data || []);

      // ✅ auto-select first customer
      if (customerRes.data?.length > 0) {
        const firstCustomerId = customerRes.data[0].tb_customer_id;

        setFormData((prev) => ({
          ...prev,
          customer_id: firstCustomerId,
          site_id: "" // reset site
        }));
      }
    } catch (err) {
      toast.error("Failed to load customers");
    } finally {
      setCustomerLoading(false);
    }
  };

  useEffect(() => {
    if (!formData.customer_id) {
      setSites([]);
      return;
    }

    const fetchSitesAndContacts = async () => {
      setSiteLoading(true);
      try {
        // 1️⃣ Fetch all sites for the customer (dropdown)
        const siteRes = await axios.get(`${API_URL}/api/get_customer_sites/${formData.customer_id}/`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }, data: {},
        });
        const sitesData = siteRes.data.data || [];

        // 2️⃣ Fetch all sites with their contacts using new API
        const contactRes = await axios.get(`${API_URL}/api/get_all_sites_with_contacts/`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }, data: {},
        });
        const sitesWithContacts = contactRes.data.data || [];

        // 3️⃣ Merge contact counts into the dropdown sites
        const enrichedSites = sitesData.map((site) => {
          const siteContact = sitesWithContacts.find(s => s.site_id === site.site_id);
          return {
            ...site,
            contactCount: siteContact?.contactCount || 0
          };
        });

        setSites(enrichedSites);
        if (enrichedSites.length > 0) {
          setFormData((prev) => ({
            ...prev,
            site_id: enrichedSites[0].site_id
          }));
        }
      } catch (err) {
        console.error("Error fetching sites or contacts:", err);
        toast.error("Failed to load sites or contact info");
      } finally {
        setSiteLoading(false);
      }
    };

    fetchSitesAndContacts();
  }, [API_URL, formData.customer_id, token]);

  // const handleChange = (e) => {
  //   const { name, value, type, checked } = e.target;
  //   setFormData((prev) => ({
  //     ...prev,
  //     [name]: type === "checkbox" ? checked : value,
  //   }));
  // };


  // ✅ Validation regex
  const nameRegex = /^[A-Za-z]+( [A-Za-z]+)*$/;
  // Email: standard format, 2–50 characters
  // const emailRegex = /^[^\s@]{2,50}@[^\s@]+\.[^\s@]+$/;
  const emailRegex = /^(?=.{2,50}$)[a-zA-Z0-9]+([._%+-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
  const phoneRegex = /^[6-9]\d{9}$/;     // Starts with 6-9, exactly 10 digits
  // Allow letters + spaces, 2–20 chars
  const designationRegex = /^[A-Za-z]{2,20}( [A-Za-z]{1,20})*$/;

  const validateForm = () => {
    const tempErrors = {};

    // Site must be selected
    if (!formData.site_id) tempErrors.site_id = "Select a site";

    // Name validation
    if (!formData.name.trim()) {
      tempErrors.name = "Name required";
    } else if (formData.name.length < 2 || formData.name.length > 40) {
      tempErrors.name = "Name must be 2–40 characters";
    } else if (!nameRegex.test(formData.name)) {
      tempErrors.name = "Only alphabets, one space allowed between words";
    }
    // Designation
    if (!formData.designation.trim()) {
      tempErrors.designation = "Designation required";
    } else if (!designationRegex.test(formData.designation)) {
      tempErrors.designation = "Only alphabets allowed, 2–20 chars, spaces allowed";
    }
    // Email
    if (!formData.email.trim()) tempErrors.email = "Email required";
    else if (!emailRegex.test(formData.email)) tempErrors.email = "Invalid email, 2–50 chars";

    // Phone
    if (!formData.phone_no.trim()) tempErrors.phone_no = "Phone required";
    else if (!phoneRegex.test(formData.phone_no)) tempErrors.phone_no = "Must start 6–9, 10 digits";

    // At least one notification
    if (!formData.notify_sms && !formData.notify_email)
      tempErrors.notification = "Select at least one notification";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    // Only alphabets for Name
    if (name === "name") {
      newValue = newValue
        .replace(/[^A-Za-z\s]/g, "")   // allow only letters + spaces
        .replace(/^\s+/, "")           // remove leading spaces
        .replace(/\s{2,}/g, " ");      // allow only single space between words

      // limit length
      if (newValue.length > 40) newValue = newValue.slice(0, 40);
    }
    // Trim spaces for Designation and Name while typing
    if (name === "designation") {
      // Remove invalid chars (only letters + spaces)
      newValue = newValue.replace(/[^A-Za-z\s]/g, "");

      // Remove leading spaces
      newValue = newValue.replace(/^\s+/, "");

      // Prevent multiple spaces
      newValue = newValue.replace(/\s{2,}/g, " ");

      // Limit total length to 20 characters
      if (newValue.length > 20) newValue = newValue.slice(0, 20);
    }

    // Remove starting/ending spaces for Email
    if (name === "email") {
      // Remove leading/trailing spaces
      newValue = newValue.replace(/^\s+|\s+$/g, "").toLowerCase();

      // Limit total length to 50
      if (newValue.length > 50) newValue = newValue.slice(0, 50);
    }
    // Only digits for phone
    if (name === "phone_no") newValue = newValue.replace(/\D/g, "").slice(0, 10);

    setFormData(prev => ({ ...prev, [name]: newValue }));

    // Real-time validation
    let error = "";

    if (name === "name") {
      if (!newValue) error = "Name is required";
      else if (newValue.length < 2 || newValue.length > 40)
        error = "Name must be 2–40 characters";
      else if (!nameRegex.test(newValue))
        error = "Only alphabets, one space allowed between words";
    }

    if (name === "designation") {
      if (!newValue) error = "Designation required";
      else if (!designationRegex.test(newValue))
        error = "Only alphabets allowed, 2–20 chars, spaces allowed";
    }
    if (name === "email") {
      if (!newValue) error = "Email required";
      else if (!emailRegex.test(newValue)) error = "Invalid email, 2–50 chars";
    }
    if (name === "phone_no") {
      if (!newValue) error = "Phone required";
      else if (!phoneRegex.test(newValue)) error = "Must start 6–9 and 10 digits";
    }
    if ((name === "notify_sms" || name === "notify_email") && !formData.notify_sms && !formData.notify_email && !checked) {
      error = "Select at least one notification";
    }

    setErrors(prev => ({ ...prev, [name]: error }));
  };




  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      // Map front-end flags to backend model fields
      const payload = {
        ...formData,
        is_sms_enabled: formData.notify_sms,
        is_email_enabled: formData.notify_email,
      };

      // Remove old keys so backend doesn't get duplicate
      delete payload.notify_sms;
      delete payload.notify_email;

      await axios.post(
        `${API_URL}/api/create_site_alert_contact/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      toast.dismiss(); // clears previous toasts

      toast.success("Alert contact created!", {
        toastId: "create-contact", // prevents duplicate toast
      });

      if (onCreated) {
        await onCreated();
      }

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to create contact");
    }
  };

  return (
    <div className="edit-site-modal">
      <Box component="form" className="edit-site-container edit-site-form" onSubmit={handleSubmit}>
        <Typography className="edit-site-title">Create Site Member</Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Customer</InputLabel>
          <Select
            name="customer_id"
            value={formData.customer_id}
            onChange={handleChange}
            label="Select Customer"
            disabled={customerLoading}
          >
            {customers.map((customer) => (
              <MenuItem
                key={customer.tb_customer_id}
                value={customer.tb_customer_id}
              >
                {customer.customer_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth error={!!errors.site_id} sx={{ mb: 2 }}>
          <InputLabel id="site-select-label">Select Site</InputLabel>
          <Select
            labelId="site-select-label"
            name="site_id"
            value={formData.site_id}
            onChange={handleChange}
            label="Select Site"
            disabled={siteLoading}
          >
            {sites.map((site) => {
              // Logic: If count is 2 or more, mark as full
              const isFull = (site.contactCount || 0) >= 2;

              return (
                <MenuItem
                  key={site.site_id}
                  value={site.site_id}
                  // This is the core prop that makes it read-only
                  disabled={isFull}
                  sx={{
                    justifyContent: "space-between",
                    // Styling the "Disabled" state specifically
                    "&.Mui-disabled": {
                      backgroundColor: "#f5f5f5 !important", // Light grey background
                      color: "#999 !important",             // Faded text
                      cursor: "not-allowed !important",     // Shows the "block" icon on hover
                      pointerEvents: "auto !important",      // IMPORTANT: Allows the cursor to change
                      opacity: 1                             // Keeps text readable but clearly "off"
                    }
                  }}
                >
                  <Typography variant="body1">{site.name}</Typography>

                  {isFull && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#ff9800",
                        fontWeight: "bold",
                        fontStyle: "italic"
                      }}
                    >
                      (Max Contacts Reached)
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </Select>
          {errors.site_id && (
            <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1 }}>
              {errors.site_id}
            </Typography>
          )}
        </FormControl>
        <TextField
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label="Designation"
          name="designation"
          value={formData.designation}
          onChange={handleChange}
          error={!!errors.designation}
          helperText={errors.designation}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label="Email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label="Phone"
          name="phone_no"
          value={formData.phone_no}
          onChange={handleChange}
          error={!!errors.phone_no}
          helperText={errors.phone_no}
          fullWidth
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>Notify Via:</Typography>
        <FormGroup row sx={{ mb: 1 }}>
          <FormControlLabel
            control={<Checkbox name="notify_sms" checked={formData.notify_sms} onChange={handleChange} />}
            label="SMS"
          />
          <FormControlLabel
            control={<Checkbox name="notify_email" checked={formData.notify_email} onChange={handleChange} />}
            label="Email"
          />
        </FormGroup>
        {errors.notification && (
          <Typography color="error" variant="caption" sx={{ display: 'block', mb: 2 }}>
            {errors.notification}
          </Typography>
        )}

        <div className="edit-btn-wrapper">
          <button type="button" className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="edit-btn">
            Create Contact
          </button>
        </div>
        {/* <ToastContainer position="top-right" autoClose={3000} /> */}
      </Box>
    </div>
  );
};

export default CreateSiteMember;